import { useEffect, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { motion, useReducedMotion } from 'framer-motion'
import {
  altColorDarkText,
  altColorLighter,
  helperTextColor,
  mainColorLighter,
} from 'src/ui/theme/tokens'
import { err, split } from 'src/utils/functions'
import {
  DiscoveryHeading,
  DiscoveryFilters,
  DiscoveryCard,
  DiscoveryLoading,
  DiscoveryEmpty,
  DiscoveryPagination,
} from '@components/common/Discovery'
import PollDesign from '@components/common/PollDesign'
import { Poll } from '../api/types/polls'
import { getPollCategories, getPolls, getPollTags } from 'src/api/polls'
type PollFilter = 'open' | 'collecting' | 'my_votes' | 'my_polls' | 'new' | 'popular' | 'rating' | 'questions'

export default function PollsSection({
  userId,
  onPollSelect,
  onPollVote,
  onPollPlay,
  refreshKey,
  sounds,
}: {
  userId?: number
  onPollSelect?: (poll: Poll) => void
  onPollVote?: (poll: Poll, hasVoted: boolean) => void
  onPollPlay?: (poll: Poll) => void
  refreshKey?: number
  sounds?: { click?: () => void }
}): JSX.Element {
  const reducedMotion = useReducedMotion()
  const [polls, setPolls] = useState<Poll[] | null>(null)
  const [pollsTotal, setPollsTotal] = useState<number>(0)
  const [tagOptions, setTagOptions] = useState<string[] | undefined>()
  const [categoryOptions, setCategoryOptions] = useState<string[] | undefined>()
  const trimMetaLabel = (value?: string) => (value || '').trim().slice(0, 16)
  const sortMetaBySelection = (values: string[], selected?: string[]) => {
    if (!selected?.length) return values
    const selectedSet = new Set(selected)
    return values.slice().sort((a, b) => {
      const aSel = selectedSet.has(a)
      const bSel = selectedSet.has(b)
      if (aSel === bSel) return 0
      return aSel ? -1 : 1
    })
  }
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [filter, setFilter] = useState<PollFilter | string>('popular')
  const [page, setPage] = useState(0)
  const [limit] = useState(10)
  const [loading, setLoading] = useState(true)
  const canUseMyPolls = Boolean(userId)

  const handleClearSearch = () => {
    setSearch('')
    setSelectedTags([])
    setSelectedCategories([])
    setFilter('popular')
    setPage(0)
  }

  const normalizeSearchToken = (token: string) => token.toLowerCase().replace(/[^a-z0-9]/g, '')
  const searchWords = debouncedSearch
    ? debouncedSearch
        .split(/\s+/)
        .map((word) => normalizeSearchToken(word))
        .filter(Boolean)
    : []

  const renderHighlightedTitle = (title: string) => {
    if (!searchWords.length) {
      return title
    }

    const tokens = title.split(/(\s+)/)
    const segments: { text: string; highlight: boolean }[] = []

    tokens.forEach((token) => {
      if (!token || /^\\s+$/.test(token)) {
        segments.push({ text: token, highlight: false })
        return
      }
      const normalized = normalizeSearchToken(token)
      if (!normalized) {
        segments.push({ text: token, highlight: false })
        return
      }

      const ranges: Array<[number, number]> = []
      searchWords.forEach((word) => {
        if (!word) return
        let start = 0
        const lower = normalized.toLowerCase()
        const needle = word.toLowerCase()
        while (true) {
          const idx = lower.indexOf(needle, start)
          if (idx === -1) break
          ranges.push([idx, idx + needle.length])
          start = idx + needle.length
        }
      })

      if (!ranges.length) {
        segments.push({ text: token, highlight: false })
        return
      }

      ranges.sort((a, b) => a[0] - b[0])
      const merged: Array<[number, number]> = []
      ranges.forEach(([start, end]) => {
        const last = merged[merged.length - 1]
        if (!last || start > last[1]) {
          merged.push([start, end])
        } else {
          last[1] = Math.max(last[1], end)
        }
      })

      let cursor = 0
      merged.forEach(([start, end]) => {
        if (start > cursor) {
          segments.push({ text: token.slice(cursor, start), highlight: false })
        }
        segments.push({ text: token.slice(start, end), highlight: true })
        cursor = end
      })
      if (cursor < token.length) {
        segments.push({ text: token.slice(cursor), highlight: false })
      }
    })

    return segments.map((segment, idx) => (
      <Box
        component="span"
        key={`${segment.text}-${idx}`}
        sx={
          segment.highlight
            ? {
                backgroundColor: 'rgba(255, 245, 200, 1)',
                color: altColorDarkText,
                borderRadius: '24px',
                // px: 0.5,
                // py: 0.15,
              }
            : undefined
        }
      >
        {segment.text}
      </Box>
    ))
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (filter === 'my_polls' && !canUseMyPolls) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFilter('open')
      return
    }
    const tags = selectedTags
    const categories = selectedCategories
    const states = filter === 'open' ? ['results_open'] : undefined
    const sort = ['popular', 'rating', 'new'].includes(filter) ? filter : undefined

    const creatorId = filter === 'my_polls' && canUseMyPolls ? userId : undefined
    const participantKeys = Array.from({ length: localStorage.length }, (_, index) =>
      localStorage.key(index)
    )
      .filter((key): key is string => !!key && /^poll_guest_key_\d+$/.test(key))
      .map((key) => localStorage.getItem(key))
      .filter((key): key is string => !!key && /^[a-zA-Z0-9_-]{8,255}$/.test(key))

    let cancelled = false
    setLoading(true)
    getPolls({
      page,
      limit,
      search: debouncedSearch || undefined,
      tags: tags.length ? tags : undefined,
      categories: categories.length ? categories : undefined,
      states,
      visibility: filter === 'my_polls' || filter === 'my_votes' ? undefined : ['public'],
      sort,
      creatorId,
      participantKeys,
      onlyParticipated: filter === 'my_votes',
      pollFormat: filter === 'questions' ? 'community_answers' : undefined,
    })
      .then((res) => {
        if (cancelled) return
        setLoading(false)
        if (res.success && res.data) {
          setPolls(res.data.polls || [])
          setPollsTotal(res.data.total || 0)
        } else {
          setPolls([])
          setPollsTotal(0)
        }
      })
      .catch((e) => {
        if (cancelled) return
        setLoading(false)
        err(e)
      })
    return () => {
      cancelled = true
    }
  }, [
    page,
    limit,
    debouncedSearch,
    selectedTags,
    selectedCategories,
    filter,
    userId,
    refreshKey,
    canUseMyPolls,
  ])

  useEffect(() => {
    if (!tagOptions) {
      getPollTags()
        .then((res) => {
          if (res.success && res.data?.length) {
            setTagOptions(res.data)
          }
        })
        .catch((e) => err(e))
    }

    if (!categoryOptions) {
      getPollCategories()
        .then((res) => {
          if (res.success && res.data?.length) {
            setCategoryOptions(res.data)
          }
        })
        .catch((e) => err(e))
    }
  }, [tagOptions, categoryOptions])

  const displayedPolls = useMemo(() => {
    return polls || []
  }, [polls])

  return (
    <PollDesign>
      <Box data-rt-tab-section sx={{ px: { xs: 1.5, sm: 3 }, pt: 1, pb: 2 }}>
        <DiscoveryFilters
          heading={
            <DiscoveryHeading
              poll
              title={
                filter === 'my_polls' ? 'Your community polls' : 'A little opinion. A lot of fun.'
              }
              description="Vote, explore the results, or take on the community ranking."
            />
          }
          poll
          query={search}
          onQuery={(value) => {
            setSearch(value)
            setPage(0)
          }}
          categories={selectedCategories}
          categoryOptions={categoryOptions || []}
          onCategories={(value) => {
            setSelectedCategories(value)
            setPage(0)
          }}
          tags={selectedTags}
          tagOptions={tagOptions || []}
          onTags={(value) => {
            setSelectedTags(value)
            setPage(0)
          }}
          onRemove={sounds?.click}
          filters={[
            {
              label: 'Popular',
              value: 'popular',
              selected: filter === 'popular',
            },
            {
              label: 'By Rating',
              value: 'rating',
              selected: filter === 'rating',
            },
            { label: 'New', value: 'new', selected: filter === 'new' },
            { label: 'Questions', value: 'questions', selected: filter === 'questions' },
            ...(canUseMyPolls
              ? [
                  {
                    label: 'My Polls',
                    value: 'my_polls',
                    selected: filter === 'my_polls',
                  },
                ]
              : []),
            {
              label: 'My Votes',
              value: 'my_votes',
              selected: filter === 'my_votes',
            },
          ]}
          onFilter={(value) => {
            sounds?.click?.()
            setFilter(value)
            setPage(0)
          }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography fontWeight={800} fontSize={14}>
            {filter === 'my_polls' ? 'Made by you' : 'Explore the community'}
          </Typography>
          <Typography variant="caption" color={helperTextColor} aria-live="polite">
            {loading ? 'Finding polls…' : pollsTotal.toLocaleString() + ' results'}
          </Typography>
        </Box>
        {loading ? (
          <DiscoveryLoading />
        ) : !displayedPolls.length ? (
          <DiscoveryEmpty
            onClear={() => {
              sounds?.click?.()
              handleClearSearch()
            }}
          />
        ) : (
          <Box
            component={motion.div}
            key={JSON.stringify([page, debouncedSearch, selectedTags, selectedCategories, filter])}
            initial={{
              opacity: reducedMotion ? 1 : 0,
              y: reducedMotion ? 0 : 8,
            }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.16 }}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 1.5,
            }}
          >
            {displayedPolls.map((poll) => {
              const isQuestion = poll.pollFormat === 'community_answers'
              const hasVoted = !!poll.viewerSubmitted
              const canPlay =
                poll.canPlay ??
                (!isQuestion &&
                  poll.state === 'results_open' &&
                  (poll.submissions || 0) > 0 &&
                  poll.pollResultsVisibility !== 'hidden')
              return (
                <DiscoveryCard
                  key={poll.id}
                  poll
                  kindLabel={isQuestion ? 'COMMUNITY ANSWERS' : undefined}
                  title={renderHighlightedTitle(poll.prompt)}
                  badge={
                    isQuestion
                      ? poll.state === 'collecting'
                        ? poll.questionExperienceVersion === 2 ? 'Open' : 'Collecting'
                        : 'Closed'
                      : poll.visibility === 'private'
                        ? 'Private'
                        : hasVoted
                          ? 'Voted'
                          : undefined
                  }
                  plays={poll.plays || 0}
                  votes={poll.submissions || 0}
                  rateUps={poll.ratesUp || 0}
                  rateDowns={poll.ratesDown || 0}
                  metadata={[
                    ...sortMetaBySelection(split(poll.category, ','), selectedCategories).map(
                      (value) => (
                        <Chip
                          key={'category-' + value}
                          size="small"
                          label={trimMetaLabel(value)}
                          onClick={() => {
                            sounds?.click?.()
                            setSelectedCategories(
                              Array.from(new Set([...selectedCategories, value]))
                            )
                            setPage(0)
                          }}
                          sx={{
                            maxWidth: '100%',
                            background: mainColorLighter,
                            fontSize: 11,
                          }}
                        />
                      )
                    ),
                    ...sortMetaBySelection(split(poll.tags, ','), selectedTags).map((value) => (
                      <Chip
                        key={'tag-' + value}
                        size="small"
                        label={trimMetaLabel(value)}
                        onClick={() => {
                          sounds?.click?.()
                          setSelectedTags(Array.from(new Set([...selectedTags, value])))
                          setPage(0)
                        }}
                        sx={{
                          maxWidth: '100%',
                          background: altColorLighter,
                          color: altColorDarkText,
                          fontSize: 11,
                        }}
                      />
                    )),
                  ]}
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      if (filter === 'my_polls' || hasVoted || isQuestion) onPollSelect?.(poll)
                      else if (onPollVote) onPollVote(poll, hasVoted)
                      else onPollSelect?.(poll)
                    }}
                  >
                    {filter === 'my_polls'
                      ? 'Edit'
                      : hasVoted
                        ? 'View'
                        : isQuestion
                          ? poll.canSubmitAnswer
                            ? 'Answer'
                            : 'View'
                          : 'Vote'}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!canPlay}
                    onClick={() => onPollPlay?.(poll)}
                  >
                    Play
                  </Button>
                </DiscoveryCard>
              )
            })}
          </Box>
        )}
        {!loading && (
          <DiscoveryPagination
            page={page}
            total={pollsTotal}
            limit={limit}
            onPage={(value) => {
              sounds?.click?.()
              setPage(value)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        )}
      </Box>
    </PollDesign>
  )
}
